import { type OutcomeType } from "../../../../../models/audit/attributeTestColumn";

/**
 * The answers an analytics conclusion may carry.
 *
 * The label names the answer and the description says what it means — the
 * pairing, and the wording, an attribute-test conclusion's dropdown already
 * offers, so the two kinds read as one vocabulary. One sheet shows both this
 * list and the outcome badge, so an option worded as the explanation alone
 * would have an auditor choosing "Issues found" and reading back "Fail".
 *
 * `not_applicable` is the one answer the badge abbreviates, to "N/A"; the long
 * form is what every dropdown offering it says, and matching the dropdowns is
 * what keeps one auditor's choice worded the same across both kinds.
 */
export const OUTCOME_OPTIONS = [
  { value: "pass", label: "Pass", description: "No issues found" },
  { value: "fail", label: "Fail", description: "Issues found" },
  {
    value: "not_applicable",
    label: "Not Applicable",
    description: "Test does not apply",
  },
] as const satisfies readonly {
  value: OutcomeType;
  label: string;
  description: string;
}[];

/**
 * Fails the build at this file the day an `OutcomeType` member is added without
 * a row above, rather than blanking a dropdown at runtime. The dialog resolves
 * an option by `find((o) => o.value === outcome)?.label ?? ""`, so an unlisted
 * member finds nothing, falls through to `""`, and renders an empty trigger —
 * with no type error at the miss, because `""` is a valid string.
 *
 * The two clauses guard opposite directions: `satisfies` holds every listed
 * value to being a real member, and this holds every member to being listed.
 *
 * `satisfies` rather than a `{ value: OutcomeType; … }[]` annotation is what
 * keeps the second direction working. An annotation widens `value` to the whole
 * union, so `Exclude` subtracts the union from itself, leaves `never`, and the
 * assertion passes for every input. Contextual typing from `satisfies` keeps
 * each `value` at its literal instead. `as const` is not what preserves them —
 * it freezes the list and its labels, which this check does not need.
 */
type AssertAllOutcomesOffered<T extends never = never> = T;
export type OutcomeOptionsAreExhaustive = AssertAllOutcomesOffered<
  Exclude<OutcomeType, (typeof OUTCOME_OPTIONS)[number]["value"]>
>;
